import React, { useState, useMemo } from 'react';
import { Modal } from '../styled-primitives';
import { FjInput } from '../primitives';
import { fjModalSizes } from '../primitives/fj-modal';
import { formatDate } from '../../utils';

type CreateEotModalProps = {
  device: Device;
  defaultDays: number;
  productName: string;
  isFlowJo: boolean;
  acceptCallback: () => void;
  cancelCallback: () => void;
};

const CONFIRMATION_PHRASE = 'I UNDERSTAND';

// Helper function to get identifier display string
const getIdentifierDisplay = (identifiers: Identifiers): string => {
  if (identifiers.hwas) {
    return identifiers.hwas.join(', ');
  }
  return identifiers.fingerprint;
};

export const CreateEotModal: React.FC<CreateEotModalProps> = ({
  device,
  defaultDays,
  productName,
  isFlowJo,
  acceptCallback,
  cancelCallback,
}: CreateEotModalProps) => {
  const [confirmationText, setConfirmationText] = useState('');
  const deviceVersion = 'hwas' in device.identifiers ? 'v10' : 'v11';
  const isConfirmed = confirmationText.trim().toUpperCase() === CONFIRMATION_PHRASE;

  const expirationDate = useMemo(() => {
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + defaultDays);
    return formatDate(endDate);
  }, [defaultDays]);

  return (
    <Modal
      dataTestId="create-eot-modal"
      cancel={{
        text: 'Cancel',
        callback: cancelCallback,
      }}
      accept={{
        text: 'Create EOT',
        callback: acceptCallback,
        disabled: !isConfirmed,
      }}
      title="Create an Extended Offline Token"
      size={fjModalSizes.LG}
      showX
      onClickX={cancelCallback}
    >
      <div style={{ lineHeight: '1.6' }}>
        <p style={{ marginBottom: '16px' }}>
          Once it is created, an Extended Offline Token (EOT) allows one computer to use{' '}
          {productName} without an internet connection for {defaultDays} days.
        </p>

        <p style={{ marginBottom: '8px' }}>
          <strong>Important notes:</strong>
        </p>
        <ul style={{ marginBottom: '16px', paddingLeft: '24px' }}>
          <li style={{ marginBottom: '8px' }}>
            Creating an EOT requires the computer to have an internet connection.
          </li>
          <li style={{ marginBottom: '8px' }}>
            Creating an EOT prevents other devices from being authorized to use {productName} during
            the {defaultDays} day period.
          </li>
          <li style={{ marginBottom: '8px' }}>
            An EOT cannot be revoked. It will expire after {defaultDays} days.
          </li>
          <li style={{ marginBottom: '8px' }}>
            For shorter offline periods, all authorized devices permit a 24-hour offline period
            without an EOT. Simply sign in to {productName} from your device less than 24 hours
            before your offline session will end to use {productName} during your brief offline
            sessions.
          </li>
        </ul>

        {isFlowJo && (
          <p style={{ marginBottom: '16px' }}>
            For more information, visit{' '}
            <a
              href="https://docs.flowjo.com/portal/flowjo-portal-offline-user-14-day-token/"
              target="_blank"
              rel="noopener noreferrer"
            >
              docs.flowjo.com
            </a>
          </p>
        )}

        <p style={{ marginBottom: '16px' }}>
          Do you want to create an extended offline token for{' '}
          <strong>
            {device.name} ({getIdentifierDisplay(device.identifiers)})
          </strong>{' '}
          and prevent other devices from authorizing for the next {defaultDays} days?
        </p>

        <div
          style={{
            marginTop: '20px',
            padding: '16px',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffca2c',
            borderRadius: '4px',
          }}
        >
          <p style={{ marginBottom: '12px', fontWeight: 'bold' }}>
            I understand I will only be able to access {productName}
            {isFlowJo && ` ${deviceVersion}`} on device ({device.name}) for the duration of the{' '}
            {defaultDays} day EOT period. I understand I WILL NOT be able to use the product on
            other devices until {expirationDate}.
          </p>
          <p style={{ marginBottom: '8px', fontSize: '0.9em' }}>
            To confirm you have read and understood the above, please type{' '}
            <strong>{CONFIRMATION_PHRASE}</strong> below:
          </p>
          <FjInput
            value={confirmationText}
            onChange={setConfirmationText}
            placeholder={`Type "${CONFIRMATION_PHRASE}" to confirm`}
            data-testid="confirm-understanding-input"
            autoFocus
          />
        </div>
      </div>
    </Modal>
  );
};
